import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  Tooltip,
  Divider,
  IconButton
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import dayjs, { Dayjs } from 'dayjs';
import { parseBackendDate } from './utils';

interface Task {
  id: string;
  title: string;
  status: string;
  endDate?: string;
}

interface TaskListProps {
  tasks: Task[];
  onToggleStatus: (task: Task) => void;
  onDelete: (taskId: string) => void;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'FINISHED': return 'success';
    case 'STARTED': return 'warning';
    default: return 'error';
  }
};

const TaskList: React.FC<TaskListProps> = ({ tasks, onToggleStatus, onDelete }) => {
  return (
    <List sx={{ pt: 0, maxHeight: 310, overflowY: 'auto' }}>
      {tasks.map((task: any) => {
        const taskDate = parseBackendDate(task.endDate);
        return (
          <React.Fragment key={task.id}>
            <ListItem
              disablePadding
              sx={{ py: 1.5 }}
              secondaryAction={
                <IconButton edge="end" size="small" onClick={() => onDelete(task.id)}>
                  <DeleteIcon fontSize="inherit" />
                </IconButton>
              }
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 4 }}>
                <Tooltip title="Toggle Status">
                  <Box
                    onClick={() => onToggleStatus(task)}
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      bgcolor: `${getStatusColor(task.status)}.main`,
                      cursor: 'pointer',
                      flexShrink: 0,
                      boxShadow: 1
                    }}
                  />
                </Tooltip>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      textDecoration: task.status === 'FINISHED' ? 'line-through' : 'none',
                      opacity: task.status === 'FINISHED' ? 0.6 : 1,
                      fontWeight: 'medium'
                    }}
                  >
                    {task.title}
                  </Typography>
                  {taskDate && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, color: 'text.secondary' }}>
                      <CalendarTodayIcon sx={{ fontSize: '0.75rem' }} />
                      <Typography variant="caption">
                        Due: {taskDate.format('D MMM YYYY')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </ListItem>
            <Divider component="li" />
          </React.Fragment>
        );
      })}
    </List>
  );
};

export default TaskList;
